/**
 * Collection filters.
 *
 * The form works on its own with a plain GET submit. This upgrades it: changes
 * are debounced, the section is fetched and the grid and filter panel are
 * swapped in, and the URL is kept in step so a filtered view stays shareable
 * and the back button behaves.
 */

class MidasFacets extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;

    this.sectionId = this.dataset.section;
    this.debounce = null;

    this.form.addEventListener('input', (event) => {
      const wait = event.target.type === 'number' ? 500 : 0;
      clearTimeout(this.debounce);
      this.debounce = setTimeout(() => this.apply(), wait);
    });

    // Stop the no-JS submit path from double firing
    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.apply();
    });

    this.onPopState = this.onPopState.bind(this);
    window.addEventListener('popstate', this.onPopState);
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.onPopState);
    clearTimeout(this.debounce);
  }

  get results() {
    return document.getElementById('CollectionGrid');
  }

  buildSearchParams() {
    const data = new FormData(this.form);
    const params = new URLSearchParams();

    for (const [key, value] of data.entries()) {
      if (value === '' || value == null) continue;
      params.append(key, value);
    }
    return params.toString();
  }

  apply() {
    const search = this.buildSearchParams();
    const url = `${this.form.action}${search ? `?${search}` : ''}`;
    history.pushState({ search }, '', url);
    this.render(search);
  }

  onPopState(event) {
    this.render(event.state?.search ?? window.location.search.replace(/^\?/, ''));
  }

  async render(search) {
    const results = this.results;
    results?.classList.add('is-loading');

    try {
      const url = `${window.location.pathname}?${search}${search ? '&' : ''}section_id=${this.sectionId}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error(`filter request failed: ${response.status}`);

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fresh = html.getElementById('CollectionGrid');
      if (!fresh) throw new Error('filter response had no grid');

      const focused = document.activeElement?.name;
      results.innerHTML = fresh.innerHTML;
      results.dataset.productCount = fresh.dataset.productCount;
      const count = document.querySelector('[data-collection-product-count]');
      if (count) count.textContent = fresh.dataset.productCount;

      // Query the document, not `this` — the swap detaches this instance and
      // a fresh midas-facets is upgraded in its place.
      if (focused) {
        document.querySelector(`#CollectionGrid [name="${CSS.escape(focused)}"]`)?.focus();
      }
    } catch (error) {
      // Fall back to a full navigation rather than leaving a stale grid
      console.error(error);
      window.location.search = search;
    } finally {
      results?.classList.remove('is-loading');
    }
  }
}

customElements.define('midas-facets', MidasFacets);
